import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { AlertCircle, Contact, X } from "lucide-react";
import { AddExternalContactRequestSchema } from "@bliks-io/contracts";
import { Alert, AlertDescription } from "../../base/alert/alert";
import { DialogShellHeader } from "../dialog-shell/DialogShellHeader";
import { DialogShellBody } from "../dialog-shell/DialogShellBody";
import { DialogShellFooter } from "../dialog-shell/DialogShellFooter";
import { Input } from "../../base/input/input";
import { Textarea } from "../../base/textarea/textarea";
import { CreatableCombobox } from "../combobox/creatable-combobox";
import { Field, FieldContent, FieldError, FieldLabel, FieldSeparator } from "../field/field";
import { FormDialogShell } from "./FormDialogShell";
export function ExternalContactFormDialog({ open, onOpenChange, onSubmit, contact, isSubmitting = false, error, onErrorDismiss, onSetErrorReady, existingContacts, initialName }) {
    const { t } = useTranslation();
    const isEditing = !!contact;
    const defaultContactTypes = useMemo(()=>[
            t("organization.externalContacts.types.customer"),
            t("organization.externalContacts.types.partner"),
            t("organization.externalContacts.types.vendor"),
            t("organization.externalContacts.types.other")
        ], [
        t
    ]);
    const { handleSubmit, reset, control, setError, formState: { isDirty, dirtyFields } } = useForm({
        resolver: zodResolver(AddExternalContactRequestSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: "",
            type: "",
            description: ""
        }
    });
    useEffect(function exposeSetErrorToParent() {
        if (onSetErrorReady) {
            onSetErrorReady(setError);
        }
    }, [
        setError,
        onSetErrorReady
    ]);
    const availableTypes = useMemo(()=>{
        const existingTypes = existingContacts?.map((c)=>c.type).filter(Boolean) || [];
        const allTypes = [
            ...new Set([
                ...defaultContactTypes,
                ...existingTypes
            ])
        ];
        return allTypes.sort((a, b)=>a.localeCompare(b));
    }, [
        existingContacts,
        defaultContactTypes
    ]);
    useEffect(function resetFormWhenDialogOpens() {
        if (open) {
            reset({
                name: contact?.name || initialName || "",
                type: contact?.type || "",
                description: contact?.description || ""
            });
        }
    }, [
        open,
        contact,
        initialName,
        reset
    ]);
    const handleFormSubmit = (data)=>{
        if (isEditing && !isDirty) {
            onOpenChange(false);
            return;
        }
        if (!isEditing) {
            onSubmit(data);
            return;
        }
        const changed = {};
        Object.keys(dirtyFields).forEach((key)=>{
            if (!dirtyFields[key]) return;
            changed[key] = data[key];
        });
        onSubmit(changed);
    };
    return /*#__PURE__*/ _jsx(FormDialogShell, {
        open: open,
        onOpenChange: onOpenChange,
        isDirty: isDirty,
        isPending: isSubmitting,
        size: "md",
        children: ({ close })=>/*#__PURE__*/ _jsxs("form", {
                noValidate: true,
                onSubmit: handleSubmit(handleFormSubmit),
                className: "flex flex-1 min-h-0 flex-col",
                children: [
                    /*#__PURE__*/ _jsx(DialogShellHeader, {
                        icon: /*#__PURE__*/ _jsx(Contact, {
                            className: "h-5 w-5"
                        }),
                        iconTone: "green",
                        title: isEditing ? t("organization.externalContacts.editContact") : t("organization.externalContacts.createContact"),
                        subtitle: isEditing ? t("organization.externalContacts.editContactDescription") : t("organization.externalContacts.createContactDescription"),
                        onClose: close
                    }),
                    /*#__PURE__*/ _jsxs(DialogShellBody, {
                        children: [
                            error && /*#__PURE__*/ _jsxs(Alert, {
                                variant: "destructive",
                                children: [
                                    /*#__PURE__*/ _jsx(AlertCircle, {
                                        className: "h-4 w-4"
                                    }),
                                    /*#__PURE__*/ _jsxs(AlertDescription, {
                                        className: "flex items-start justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "flex-1",
                                                children: error
                                            }),
                                            onErrorDismiss && /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: onErrorDismiss,
                                                className: "shrink-0 rounded-xs opacity-70 transition-opacity hover:opacity-100",
                                                "aria-label": t("common.dismiss"),
                                                children: /*#__PURE__*/ _jsx(X, {
                                                    className: "h-4 w-4"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-8",
                                children: [
                                    /*#__PURE__*/ _jsx(Controller, {
                                        name: "name",
                                        control: control,
                                        render: ({ field, fieldState })=>/*#__PURE__*/ _jsxs(Field, {
                                                "data-invalid": !!fieldState.error,
                                                children: [
                                                    /*#__PURE__*/ _jsxs(FieldLabel, {
                                                        children: [
                                                            t("organization.externalContacts.nameLabel"),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "text-destructive ml-1",
                                                                children: "*"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(FieldContent, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Input, {
                                                                ...field,
                                                                value: field.value ?? "",
                                                                placeholder: t("organization.externalContacts.namePlaceholder"),
                                                                "aria-invalid": !!fieldState.error
                                                            }),
                                                            fieldState.error && /*#__PURE__*/ _jsx(FieldError, {
                                                                children: t(fieldState.error.message || "")
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-4",
                                        children: [
                                            /*#__PURE__*/ _jsx(FieldSeparator, {
                                                children: t("organization.externalContacts.typeSectionLabel")
                                            }),
                                            /*#__PURE__*/ _jsx(Controller, {
                                                name: "type",
                                                control: control,
                                                render: ({ field, fieldState })=>/*#__PURE__*/ _jsxs(Field, {
                                                        "data-invalid": !!fieldState.error,
                                                        children: [
                                                            /*#__PURE__*/ _jsxs(FieldLabel, {
                                                                children: [
                                                                    t("organization.externalContacts.typeLabel"),
                                                                    /*#__PURE__*/ _jsx("span", {
                                                                        className: "text-destructive ml-1",
                                                                        children: "*"
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(FieldContent, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(CreatableCombobox, {
                                                                        options: availableTypes,
                                                                        value: field.value,
                                                                        onValueChange: field.onChange,
                                                                        placeholder: t("organization.externalContacts.typePlaceholder")
                                                                    }),
                                                                    fieldState.error && /*#__PURE__*/ _jsx(FieldError, {
                                                                        children: t(fieldState.error.message || "")
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-4",
                                        children: [
                                            /*#__PURE__*/ _jsx(FieldSeparator, {
                                                children: t("common.Details")
                                            }),
                                            /*#__PURE__*/ _jsx(Controller, {
                                                name: "description",
                                                control: control,
                                                render: ({ field, fieldState })=>/*#__PURE__*/ _jsxs(Field, {
                                                        "data-invalid": !!fieldState.error,
                                                        children: [
                                                            /*#__PURE__*/ _jsx(FieldLabel, {
                                                                children: t("common.Description")
                                                            }),
                                                            /*#__PURE__*/ _jsxs(FieldContent, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Textarea, {
                                                                        ...field,
                                                                        value: field.value ?? "",
                                                                        rows: 4,
                                                                        placeholder: t("organization.externalContacts.descriptionPlaceholder"),
                                                                        "aria-invalid": !!fieldState.error
                                                                    }),
                                                                    fieldState.error && /*#__PURE__*/ _jsx(FieldError, {
                                                                        children: t(fieldState.error.message || "")
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    })
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DialogShellFooter, {
                        secondary: {
                            label: t("common.cancel"),
                            onClick: close,
                            disabled: isSubmitting
                        },
                        primary: {
                            label: isEditing ? t("common.save") : t("common.create"),
                            type: "submit",
                            loading: isSubmitting
                        }
                    })
                ]
            })
    });
}

//# sourceMappingURL=ExternalContactFormDialog.js.map